/**
 * BAB Report — shared number formatting.
 *
 * The scale toggle ("EUR" / "EUR '000") belongs to the report overview ONLY.
 * Drill-down modals always show full EUR via `formatEur`, so a 3'570 EUR account
 * never shows up as "4".
 */
import type { AmountScale, SignMode } from '../types/babReport';

const NUMBER_FORMAT = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const NUMBER_FORMAT_2DP = new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERCENT_FORMAT = new Intl.NumberFormat('de-CH', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Overview only: divides by 1000 when the scale is `thousands`. */
export function formatScaled(amount: number, scale: AmountScale): string {
    return NUMBER_FORMAT.format(scale === 'thousands' ? amount / 1000 : amount);
}

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Ref: Mar 2026" — the month a comment or query is ABOUT.
 *
 * Distinct from the frozen report period it was written in, and worth spelling out on every
 * card: a YTD row lists notes from every month of the year to date, and a booking note carries
 * the month of its posting date, which need not be the period on screen.
 */
export function refPeriodLabel(r: { refPeriodYear: number; refPeriodMonth: number }): string {
    return `Ref: ${MONTH_NAMES[r.refPeriodMonth - 1] ?? r.refPeriodMonth} ${r.refPeriodYear}`;
}

/** Drill-down modals: full EUR amounts, never scaled. */
export function formatEur(amount: number): string {
    return NUMBER_FORMAT.format(amount);
}

/** Full EUR with cents — used where an exact figure matters (comment scope labels). */
export function formatEurExact(amount: number): string {
    return NUMBER_FORMAT_2DP.format(amount);
}

/** The API returns percentages already multiplied by 100. */
export function formatPercent(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '-';
    return PERCENT_FORMAT.format(value / 100);
}

/** Percentage from a delta and its base; null when the base is 0 (no meaningful %). */
export function deltaPct(delta: number, base: number): number | null {
    return base !== 0 ? (delta / Math.abs(base)) * 100 : null;
}

/**
 * IBCS/Hichert delta color. signage = -1 (income/KPI): higher actual is favorable;
 * signage = +1 (cost): lower actual is favorable.
 *
 * NOTE: always feed this the NATIVE (BAB-positive) delta, never one that `applySign` has
 * already flipped — otherwise "favorable" inverts on every cost line.
 */
export function getDeltaColorClass(delta: number, signage: number): string {
    if (delta === 0) return 'text-gray-600';
    const favorable = signage < 0 ? delta > 0 : delta < 0;
    return favorable ? 'text-blue-800' : 'text-red-600';
}

/**
 * Display sign of an amount.
 *
 * The BAB shows everything positive — a cost of 70'793 reads as 70'793, and the reader is
 * expected to know that "Sales cost" is subtracted. `signed` mode instead shows income and
 * result lines positive and costs negative, which makes the column add up as written:
 * Net Sales 88'081 + COGS −70'793 = Gross Profit 17'288.
 *
 * `Signage` from [BabProcess].[LineDefinition] is exactly the income/cost marker: −1 on the
 * income and result lines (Net Sales, GP, CM I/II, EBITDA, EBIT, EBT), +1 on the cost lines.
 * The stored display amount is already `raw × signage`, so multiplying by `−signage` yields
 * the arithmetic sign. Applies unchanged to Budget, Prior Year and the deltas; percentages
 * need no flip, since numerator and denominator flip together.
 */
export function applySign(amount: number, signage: number, mode: SignMode): number {
    return mode === 'signed' ? amount * -signage : amount;
}
